import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  addItemToCart as addItemToCartAction,
  deleteItemFromCart as deleteItemFromCartAction,
} from '../../../entities/cart/model';
import {
  selectCurrentItemWithCartInfo,
  selectItemsStatus,
  getItem,
} from '../../../entities/product/model';
import { AbilityContext } from '@shared/lib/ability';
import { CatalogItemSelectionFilter } from './CatalogItemSelectionFilter';
import { CartSummary } from './CartSummary';
import { SpecificationsTable } from '../../../shared/ui/specifications-table';
import { Tabs } from '../../../shared/ui/tabs';
import { ImageWithFallback } from '../../../shared/ui/image-with-fallback';
import { useAppDispatch } from '../../../app/store/store';
import { Item } from '../../../types/interfaces';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const CatalogDetail = () => {
  const { id } = useParams();
  const product = useSelector(selectCurrentItemWithCartInfo);
  const status = useSelector(selectItemsStatus);
  const dispatch = useAppDispatch();
  const ability = useContext(AbilityContext);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  useEffect(() => {
    dispatch(getItem({ id: Number(id) }));
  }, [dispatch, id]);

  const rawPhotos = [
    product.photo,
    ...(product.items?.map((i) => i.photo).filter(Boolean) ?? []),
  ].filter((p): p is string => !!p);
  const photos = rawPhotos.filter((p, idx) => rawPhotos.indexOf(p) === idx);

  const displayPhoto = photos[selectedPhotoIndex] ?? '';

  const handlePrev = () =>
    setSelectedPhotoIndex((p) => (p > 0 ? p - 1 : photos.length - 1));
  const handleNext = () =>
    setSelectedPhotoIndex((p) => (p < photos.length - 1 ? p + 1 : 0));

  const addItemToCart = (
    id: number,
    quantity: number,
    verification_item: string | null,
    verification_quantity: number,
    passport_item: string | null = null,
    passport_quantity: number = 0
  ) => {
    dispatch(
      addItemToCartAction({ id, quantity, verification_item, verification_quantity, passport_item, passport_quantity })
    );
  };

  const removeFromCart = (id: number) => {
    dispatch(deleteItemFromCartAction(id));
  };

  const addCartLogic = () => {
    if (ability.can('add', 'cartitem')) {
      return (
        <CatalogItemSelectionFilter
          status={status}
          addToCart={addItemToCart}
          product={product}
          removeFromCart={removeFromCart}
        />
      );
    }
    return (
      product?.items?.length > 1 && (
        <div>
          <label className="block font-semibold mb-2 text-[#2d2c2c]">Товары в группе</label>
          <ul className="flex flex-col gap-2 list-none p-0">
            {product?.items?.map((item: Item) => (
              <li key={item.id}>{item.name}</li>
            ))}
          </ul>
        </div>
      )
    );
  };

  return (
    <div className="flex flex-col gap-10 mt-5 px-10 pt-10 pb-6 bg-white rounded-2xl">
      <div className="flex gap-12 flex-wrap">
        <div className="flex flex-col gap-4 w-[313px] shrink-0">
          <div className="relative bg-[#F5F6FA] rounded-2xl flex items-center justify-center h-[390px] overflow-hidden">
            {photos.length > 1 && (
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border-0 rounded-full w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#e3282a] z-10 cursor-pointer shadow-sm"
              >
                <FiChevronLeft size={16} />
              </button>
            )}
            <ImageWithFallback
              key={displayPhoto}
              src={displayPhoto || undefined}
              alt={product.name}
              className="max-w-[214px] max-h-[304px] object-contain"
            />
            {photos.length > 1 && (
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white border-0 rounded-full w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#e3282a] z-10 cursor-pointer shadow-sm"
              >
                <FiChevronRight size={16} />
              </button>
            )}
          </div>
          {photos.length > 1 && (
            <div className="grid grid-cols-3 gap-[17px]">
              {photos.map((photo, idx) => (
                <div
                  key={idx}
                  onClick={() => setSelectedPhotoIndex(idx)}
                  className={`aspect-square rounded-xl border-2 overflow-hidden cursor-pointer bg-[#F5F6FA] flex items-center justify-center transition-colors ${
                    idx === selectedPhotoIndex ? 'border-[#e3282a]' : 'border-transparent'
                  }`}
                >
                  <img src={photo} alt="" className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className="w-[541px] max-w-full flex flex-col">
            <h3 className="text-[20px] leading-[28px] font-semibold text-[#141414]">{product.name}</h3>
            <p className="text-base leading-6 text-[#6E7173] mt-3">{product.short_description}</p>
            <div className="mt-6">{addCartLogic()}</div>
          </div>
       
          {ability.can('add', 'cartitem') && (
            <div className="mt-6">
              <CartSummary product={product} status={status} />
            </div>
          )}
        </div>
      </div>

      <div>
        <Tabs variant="underline">
          <div title="Описание">
            <p className="text-sm text-gray-600 leading-relaxed">{product?.description}</p>
          </div>
          <div title="Тех.характеристики">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <SpecificationsTable data={product?.tech_specs as Record<string, any> ?? undefined} />
          </div>
          <div title="Доп.материалы">
            <ul className="list-none p-0">
              {product?.additional_materials?.map((material: any, index: number) => (
                <li key={index} className="mb-2">
                  <a href={material.material_url} target="_blank" rel="noopener noreferrer" className="text-[#e3282a] text-sm no-underline hover:underline flex items-center gap-1">
                    {material.name} <span className="text-xs">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default CatalogDetail;
